import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Check, AlertTriangle, AlertCircle, Lightbulb, TrendingUp, TrendingDown } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { getDepartmentAnalysis, type DepartmentAnalysis, type HistoricalMonth, type AnalysisStats, type ForecastStatus } from '../../api';
import { formatCurrency } from '../../utils/formatters';
import { formatPor, formatPercent } from './columnRegistry';
import { useForecastChartPalette } from '../../utils/chartPalette';

type ViewMode = '$' | 'POR' | '% of Revenue';

interface DepartmentAnalysisTabProps {
  departmentName: string;
  chartOfAccountDepartmentId?: number;
  accountType: 'Revenue' | 'Expense';
  orgId: number;
  locationId: number;
  versionId?: number;
  forecastMonth: number;
  forecastYear: number;
  driverType?: string;
  initialSelectedDepartments?: string[];
  occupiedRoomsPerMonth?: Record<string, number>;
  currency?: string | null;
}

// ============ Skeleton Component ============

function AnalysisSkeleton() {
  return (
    <div className="sk-scope space-y-4 animate-pulse" aria-busy="true">
      {/* Insight Card Skeleton */}
      <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
        <div className="h-4 bg-slate-200 rounded w-3/4 mb-3" />
        <div className="h-3 bg-slate-200 rounded w-1/2 mb-4" />
        <div className="h-8 bg-slate-200 rounded w-48" />
      </div>

      {/* Chart Section Skeleton */}
      <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
        <div className="h-3 bg-slate-200 rounded w-40 mb-4" />
        <div className="flex items-end gap-2 h-24 mb-4">
          {Array.from({ length: 13 }).map((_, i) => (
            <div
              key={i}
              className="flex-1 bg-slate-200 rounded-t"
              style={{ height: `${30 + ((i * 37) % 60)}%` }}
            />
          ))}
        </div>
        <div className="flex gap-4">
          <div className="h-3 bg-slate-200 rounded w-16" />
          <div className="h-3 bg-slate-200 rounded w-16" />
          <div className="h-3 bg-slate-200 rounded w-20" />
        </div>
      </div>

      {/* Stats Grid Skeleton */}
      <div className="grid grid-cols-2 gap-3">
        <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
          <div className="h-3 bg-slate-200 rounded w-20 mb-2" />
          <div className="h-5 bg-slate-200 rounded w-16" />
        </div>
        <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
          <div className="h-3 bg-slate-200 rounded w-20 mb-2" />
          <div className="h-5 bg-slate-200 rounded w-12" />
        </div>
      </div>
    </div>
  );
}

// ============ Mini Bar Chart Component ============

interface MiniChartProps {
  data: HistoricalMonth[];
  currentForecast: HistoricalMonth | null;
  stats: AnalysisStats;
  formatValue: (v: number) => string;
}

function MiniChart({ data, currentForecast, stats, formatValue }: MiniChartProps) {
  const [animationReady, setAnimationReady] = useState(false);
  const chartPalette = useForecastChartPalette();
  // New look: bars follow the chart palette (Classic keeps the brand tokens).
  const paletteVars = chartPalette.inset
    ? ({ '--fv3-bar': chartPalette.colors.primary, '--fv3-bar-wash': chartPalette.forecastFill } as React.CSSProperties)
    : undefined;

  useEffect(() => {
    // Trigger animation after component mounts
    const timer = setTimeout(() => {
      setAnimationReady(true);
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  // Combine historical data with forecast for display
  // Use absolute values to handle revenue accounts (stored as negative but displayed positive)
  const chartData = useMemo(() => {
    const combined = data.map(d => ({
      ...d,
      value: Math.abs(d.value),
    }));
    if (currentForecast) {
      combined.push({
        ...currentForecast,
        value: Math.abs(currentForecast.value),
      });
    }
    return combined;
  }, [data, currentForecast]);

  // Use stats for scale (already correctly calculated) and include forecast
  const forecastValue = currentForecast ? Math.abs(currentForecast.value) : 0;
  const chartMin = Math.min(stats.min, forecastValue) * 0.9; // Add 10% padding
  const chartMax = Math.max(stats.max, forecastValue) * 1.1;

  const calculateHeight = (value: number) => {
    if (chartMax === chartMin) return 50;
    return Math.max(5, ((value - chartMin) / (chartMax - chartMin)) * 100);
  };

  // Calculate range band position (typical range from stats)
  const rangeMinHeight = calculateHeight(stats.min);
  const rangeMaxHeight = calculateHeight(stats.max);
  const rangeBottom = Math.min(rangeMinHeight, rangeMaxHeight);
  const rangeHeight = Math.abs(rangeMaxHeight - rangeMinHeight);

  return (
    <div className="p-4 bg-slate-50 rounded-lg border border-slate-200" style={paletteVars}>
      <div className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-3">
        Last 12 Months + Forecast
      </div>

      <div className="relative h-24 mb-3">
        {/* Range band (typical range) */}
        <div
          className="absolute left-0 right-0 bg-blue-100/50 border-t border-b border-blue-200 border-dashed"
          style={{
            bottom: `${rangeBottom}%`,
            height: `${rangeHeight}%`,
          }}
        />

        {/* Bars */}
        <div className="relative h-full flex items-end gap-1">
          {chartData.map((point, index) => {
            const height = calculateHeight(point.value);
            const delay = index * 40;

            return (
              <div
                key={`${point.month}-${point.isForecast}`}
                className="flex-1 flex flex-col justify-end h-full"
              >
                <div
                  className="w-full rounded-t transition-all duration-400 ease-out"
                  style={{
                    height: animationReady ? `${height}%` : '0%',
                    transitionDelay: `${delay}ms`,
                    minHeight: point.value > 0 ? '4px' : '0px',
                    background: point.isForecast ? 'var(--fv3-bar-wash, var(--lodge-orange-100))' : 'var(--fv3-bar, var(--lodge-orange-400))',
                    border: point.isForecast ? '2px dashed var(--fv3-bar, var(--lodge-orange-400))' : undefined,
                  }}
                  title={`${point.displayMonth}: ${formatValue(point.value)}`}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* X-axis labels */}
      <div className="flex gap-1">
        {chartData.map((point) => (
          <div
            key={`label-${point.month}`}
            className={`flex-1 text-center text-[10px] truncate ${point.isForecast ? 'font-semibold' : 'text-slate-400'}`}
            style={point.isForecast ? { color: 'var(--fv3-bar, var(--lodge-orange-500))' } : undefined}
          >
            {point.displayMonth}
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 mt-3 pt-3 border-t border-slate-200">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded" style={{ background: 'var(--fv3-bar, var(--lodge-orange-400))' }} />
          <span className="text-xs text-slate-500">Actual</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded" style={{ background: 'var(--fv3-bar-wash, var(--lodge-orange-100))', border: '2px dashed var(--fv3-bar, var(--lodge-orange-400))' }} />
          <span className="text-xs text-slate-500">Forecast</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-blue-100 border border-blue-200" />
          <span className="text-xs text-slate-500">Typical Range</span>
        </div>
      </div>
    </div>
  );
}

// ============ Insight Card Component ============

interface InsightCardProps {
  insight: DepartmentAnalysis['insight'];
  forecastStatus: DepartmentAnalysis['forecastStatus'];
}

function InsightCard({ insight, forecastStatus }: InsightCardProps) {
  const getStatusIcon = () => {
    switch (insight.statusType) {
      case 'success':
        return <Check size={16} className="text-green-600" />;
      case 'warning':
        return <AlertTriangle size={16} className="text-amber-600" />;
      case 'danger':
        return <AlertCircle size={16} className="text-red-600" />;
    }
  };

  const getStatusStyles = () => {
    switch (insight.statusType) {
      case 'success':
        return 'bg-green-50 border-green-200 text-green-700';
      case 'warning':
        return 'bg-amber-50 border-amber-200 text-amber-700';
      case 'danger':
        return 'bg-red-50 border-red-200 text-red-700';
    }
  };

  // Parse the insight text to highlight the range values ($X - $Y or X% - Y%)
  const highlightedText = insight.text.replace(
    /(\$[\d,.]+[KM]?\s*-\s*\$[\d,.]+[KM]?|[\d,.]+%\s*-\s*[\d,.]+%)/g,
    '<strong class="text-slate-800">$1</strong>'
  );

  return (
    <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
      <p
        className="text-sm text-slate-600 mb-3"
        dangerouslySetInnerHTML={{ __html: highlightedText }}
      />
      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border ${getStatusStyles()}`}>
        {getStatusIcon()}
        <span className="text-sm font-medium">{insight.statusMessage}</span>
      </div>
    </div>
  );
}

// ============ Stats Grid Component ============

interface StatsGridProps {
  stats: AnalysisStats;
  accountCount: number;
  formatValue: (v: number) => string;
}

function StatsGrid({ stats, accountCount, formatValue }: StatsGridProps) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
        <div className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">
          12-Month Avg
        </div>
        <div className="text-lg font-semibold text-slate-800">
          {formatValue(stats.mean)}
        </div>
      </div>
      <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
        <div className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">
          Variance
        </div>
        <div className="text-lg font-semibold text-slate-800">
          ±{Math.round(stats.variance)}%
        </div>
      </div>
      <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 col-span-2">
        <div className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">
          Accounts in Department
        </div>
        <div className="text-lg font-semibold text-slate-800">
          {accountCount}
        </div>
      </div>
    </div>
  );
}

// ============ Context Note Component ============

interface ContextNoteProps {
  contextNote: string | null;
  statusType: 'success' | 'warning' | 'danger';
}

function ContextNote({ contextNote, statusType }: ContextNoteProps) {
  if (!contextNote) return null;

  const getBgColor = () => {
    switch (statusType) {
      case 'success':
        return 'bg-blue-50 border-blue-200';
      case 'warning':
        return 'bg-amber-50 border-amber-200';
      case 'danger':
        return 'bg-red-50 border-red-200';
    }
  };

  const getIcon = () => {
    switch (statusType) {
      case 'success':
        return <Lightbulb size={14} className="text-blue-500" />;
      case 'warning':
        return <TrendingUp size={14} className="text-amber-500" />;
      case 'danger':
        return <TrendingDown size={14} className="text-red-500" />;
    }
  };

  return (
    <div className={`p-3 rounded-lg border ${getBgColor()}`}>
      <div className="flex items-start gap-2">
        <div className="mt-0.5">{getIcon()}</div>
        <p className="text-sm text-slate-600">{contextNote}</p>
      </div>
    </div>
  );
}

// ============ Main Department Analysis Tab Component ============

export function DepartmentAnalysisTab({
  departmentName,
  chartOfAccountDepartmentId,
  accountType,
  orgId,
  locationId,
  versionId,
  forecastMonth,
  forecastYear,
  initialSelectedDepartments,
  occupiedRoomsPerMonth,
  currency,
}: DepartmentAnalysisTabProps) {
  const showToggle = accountType === 'Expense';
  const [showSkeleton, setShowSkeleton] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>('$');
  const [selectedDepts, setSelectedDepts] = useState<string[]>(
    initialSelectedDepartments ?? []
  );

  // Animation state for department chiclet panel
  const [showDepts, setShowDepts] = useState(false);
  const [visibleCount, setVisibleCount] = useState(0);
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  // Fetch analysis data
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['departmentAnalysis', orgId, locationId, departmentName, chartOfAccountDepartmentId, accountType, versionId, forecastMonth, forecastYear],
    queryFn: () => getDepartmentAnalysis({
      orgId,
      locationId,
      departmentName,
      chartOfAccountDepartmentId,
      accountType,
      versionId,
      forecastMonth,
      forecastYear,
    }),
    enabled: !!orgId && !!locationId && !!departmentName && !!forecastMonth && !!forecastYear,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // Show skeleton for minimum duration for smooth transition
  useEffect(() => {
    if (!isLoading && data) {
      const timer = setTimeout(() => {
        setShowSkeleton(false);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isLoading, data]);

  // Derive available revenue departments from API response (no extra fetch needed)
  const availableDepts = useMemo(() => {
    if (!data?.revenueByDepartmentPerMonth) return [];
    const deptSet = new Set<string>();
    Object.values(data.revenueByDepartmentPerMonth).forEach(monthData => {
      Object.keys(monthData).forEach(k => { if (k !== '_total') deptSet.add(k); });
    });
    return Array.from(deptSet).sort();
  }, [data]);

  // Transform historical months based on view mode
  const transformedMonths = useMemo(() => {
    if (!data) return [];
    return data.historicalMonths.map(m => {
      if (viewMode === 'POR') {
        const rooms = data.roomStatsPerMonth?.[m.month] ?? 0;
        return { ...m, value: rooms > 0 ? m.value / rooms : 0 };
      }
      if (viewMode === '% of Revenue') {
        const monthRevData = data.revenueByDepartmentPerMonth?.[m.month] ?? {};
        const totalRev = selectedDepts.length > 0
          ? selectedDepts.reduce((sum, d) => sum + (monthRevData[d] ?? 0), 0)
          : (monthRevData._total ?? 0);
        return { ...m, value: totalRev > 0 ? (m.value / totalRev) * 100 : 0 };
      }
      return m;
    });
  }, [data, viewMode, selectedDepts]);

  // Transform currentForecast value the same way
  const transformedForecast = useMemo(() => {
    if (!data?.currentForecast) return null;
    const fc = data.currentForecast;
    if (viewMode === 'POR') {
      // Use version occupied rooms (same source as account-level and $POR grid column)
      const MONTH_KEYS = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];
      const monthKey = MONTH_KEYS[forecastMonth - 1];
      const rooms = occupiedRoomsPerMonth?.[monthKey] ?? 0;
      return { ...fc, value: rooms > 0 ? fc.value / rooms : 0 };
    }
    if (viewMode === '% of Revenue') {
      // For the forecast bar, use forecasted revenue by department
      const forecastRevData = data.forecastRevenueByDepartment ?? {};
      const totalRev = selectedDepts.length > 0
        ? selectedDepts.reduce((sum, d) => sum + (forecastRevData[d] ?? 0), 0)
        : (forecastRevData._total ?? 0);
      return { ...fc, value: totalRev > 0 ? (fc.value / totalRev) * 100 : 0 };
    }
    return fc;
  }, [data, viewMode, selectedDepts, forecastMonth, forecastYear]);

  // Recompute stats from transformed data when not in $ mode
  const transformedStats = useMemo(() => {
    if (!data?.stats) return null;
    if (viewMode === '$') return data.stats;
    if (transformedMonths.length < 3) return null;
    const values = transformedMonths.map(m => Math.abs(m.value));
    const min = Math.min(...values);
    const max = Math.max(...values);
    const mean = values.reduce((a, b) => a + b, 0) / values.length;
    const squaredDiffs = values.map(v => Math.pow(v - mean, 2));
    const stdDev = Math.sqrt(squaredDiffs.reduce((a, b) => a + b, 0) / values.length);
    const variance = mean !== 0 ? (stdDev / Math.abs(mean)) * 100 : 0;
    return { min, max, mean, stdDev, variance: Math.round(variance * 10) / 10, range: max - min };
  }, [data, viewMode, transformedMonths]);

  // Compute insight text + forecast status for POR / % of Revenue modes
  const transformedInsight = useMemo(() => {
    if (viewMode === '$' || !transformedStats || !transformedForecast) return null;
    const fmt = viewMode === 'POR' ? formatPor : formatPercent;
    const { min, max } = transformedStats;
    const fcValue = Math.abs(transformedForecast.value);
    const minFmt = fmt(min);
    const maxFmt = fmt(max);
    const fcFmt = fmt(fcValue);
    const monthName = new Date(forecastYear, forecastMonth - 1).toLocaleString('en', { month: 'long' });
    const modeLabel = viewMode === 'POR' ? '$POR' : '% of revenue';

    const text = `This department typically books between ${minFmt} - ${maxFmt} ${modeLabel} per month based on the last 12 months.`;

    let forecastStatus: ForecastStatus = 'in_range';
    let forecastPosition = 50;
    let deviationPercent = 0;

    if (max !== min) {
      forecastPosition = Math.max(0, Math.min(100, ((fcValue - min) / (max - min)) * 100));
    }

    if (fcValue >= min && fcValue <= max) {
      forecastStatus = 'in_range';
    } else if (fcValue > max && max !== 0) {
      deviationPercent = ((fcValue - max) / max) * 100;
      forecastStatus = deviationPercent > 20 ? 'high_danger' : 'high_warning';
      forecastPosition = 100;
    } else if (fcValue < min && min !== 0) {
      deviationPercent = ((min - fcValue) / min) * 100;
      forecastStatus = deviationPercent > 20 ? 'low_danger' : 'low_warning';
      forecastPosition = 0;
    }

    let statusMessage = '';
    let statusType: 'success' | 'warning' | 'danger' = 'success';
    let contextNote = '';

    switch (forecastStatus) {
      case 'in_range':
        statusMessage = 'Current forecast is in line with this range';
        statusType = 'success';
        contextNote = forecastPosition > 75
          ? `${monthName} forecast (${fcFmt}) is at the high end of your typical range.`
          : forecastPosition < 25
          ? `${monthName} forecast (${fcFmt}) is at the low end of your typical range.`
          : `${monthName} forecast (${fcFmt}) is in the middle of your typical range.`;
        break;
      case 'high_warning':
        statusMessage = 'Current forecast is slightly above typical range';
        statusType = 'warning';
        contextNote = `${monthName} forecast (${fcFmt}) exceeds your typical high by ${Math.round(deviationPercent)}%. Consider reviewing this value.`;
        break;
      case 'high_danger':
        statusMessage = 'Current forecast is significantly above typical range';
        statusType = 'danger';
        contextNote = `${monthName} forecast (${fcFmt}) exceeds your typical high by ${Math.round(deviationPercent)}%. This is unusually high.`;
        break;
      case 'low_warning':
        statusMessage = 'Current forecast is slightly below typical range';
        statusType = 'warning';
        contextNote = `${monthName} forecast (${fcFmt}) is below your typical low by ${Math.round(deviationPercent)}%. Consider reviewing this value.`;
        break;
      case 'low_danger':
        statusMessage = 'Current forecast is significantly below typical range';
        statusType = 'danger';
        contextNote = `${monthName} forecast (${fcFmt}) is below your typical low by ${Math.round(deviationPercent)}%. This is unusually low.`;
        break;
    }

    return { insight: { text, statusMessage, statusType, contextNote }, forecastStatus };
  }, [viewMode, transformedStats, transformedForecast, forecastMonth, forecastYear]);

  // Format value based on view mode
  const formatValue = (v: number) => {
    if (viewMode === '% of Revenue') return formatPercent(v);
    if (viewMode === 'POR') return formatPor(v);
    return formatCurrency(v, true, currency);
  };

  // Sync selected departments when the department changes (panel stays mounted across department switches)
  useEffect(() => {
    setSelectedDepts(initialSelectedDepartments ?? []);
  }, [departmentName, chartOfAccountDepartmentId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear all pending animation timeouts on unmount
  useEffect(() => {
    return () => { timeoutsRef.current.forEach(clearTimeout); };
  }, []);

  const handleModeChange = (mode: ViewMode) => {
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
    setViewMode(mode);
    if (mode === '% of Revenue') {
      timeoutsRef.current.push(setTimeout(() => setShowDepts(true), 120));
      availableDepts.forEach((_, i) => {
        timeoutsRef.current.push(
          setTimeout(() => setVisibleCount(i + 1), 350 + i * 140)
        );
      });
    } else {
      setVisibleCount(0);
      timeoutsRef.current.push(setTimeout(() => setShowDepts(false), 100));
    }
  };

  const toggleDept = (dept: string) => {
    setSelectedDepts(prev =>
      prev.includes(dept) ? prev.filter(d => d !== dept) : [...prev, dept]
    );
  };

  // Loading state
  if (showSkeleton || isLoading || !data) {
    return <AnalysisSkeleton />;
  }

  // Error state
  if (isError) {
    return (
      <div className="p-4 bg-red-50 rounded-lg border border-red-200">
        <div className="flex items-center gap-2 text-red-700">
          <AlertCircle size={16} />
          <span className="text-sm font-medium">Failed to load analysis</span>
        </div>
        <p className="text-xs text-red-600 mt-1">
          {error instanceof Error ? error.message : 'Unknown error'}
        </p>
      </div>
    );
  }

  // Insufficient data state
  if (!data.stats) {
    return (
      <div className="space-y-4">
        <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
          <div className="flex items-center gap-2 text-amber-700">
            <AlertTriangle size={16} />
            <span className="text-sm font-medium">{data.insight.statusMessage}</span>
          </div>
          <p className="text-xs text-amber-600 mt-1">
            {data.insight.text}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Insight Card */}
      {viewMode === '$' ? (
        <InsightCard
          insight={data.insight}
          forecastStatus={data.forecastStatus}
        />
      ) : transformedInsight ? (
        <InsightCard
          insight={transformedInsight.insight}
          forecastStatus={transformedInsight.forecastStatus}
        />
      ) : null}

      {/* View mode toggle + animated department chiclet panel (expense departments only) */}
      {showToggle && <div className="bg-slate-100 rounded-lg p-1">
        {/* Toggle row */}
        <div className="flex items-center gap-1">
          {(['$', 'POR', '% of Revenue'] as ViewMode[]).map(mode => (
            <button
              key={mode}
              className={`flex-1 text-xs px-2 py-1.5 rounded-md font-medium transition-colors
                ${viewMode === mode
                  ? 'bg-white text-slate-800 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'}`}
              onClick={() => handleModeChange(mode)}
            >
              {mode}
            </button>
          ))}
        </div>

        {/* Expandable dept selector — animates open/shut inside the same card */}
        <div
          style={{
            maxHeight: showDepts ? '180px' : '0px',
            opacity: showDepts ? 1 : 0,
            overflow: 'hidden',
            transition: 'max-height 500ms cubic-bezier(0.4, 0, 0.2, 1), opacity 400ms ease',
          }}
        >
          <div className="pt-3 pb-1 px-1">
            <div
              style={{
                opacity: showDepts ? 1 : 0,
                transform: showDepts ? 'translateY(0)' : 'translateY(-6px)',
                transition: 'opacity 300ms ease 120ms, transform 300ms ease 120ms',
              }}
              className="text-xs font-medium text-slate-500 mb-2"
            >
              Revenue Departments
            </div>

            <div className="flex flex-wrap gap-1.5">
              {availableDepts.map((dept, i) => {
                const isVisible = i < visibleCount;
                const isSelected = selectedDepts.includes(dept);
                return (
                  <button
                    key={dept}
                    onClick={() => toggleDept(dept)}
                    style={{
                      opacity: isVisible ? 1 : 0,
                      transform: isVisible ? 'scale(1) translateY(0)' : 'scale(0.85) translateY(8px)',
                      transition: isVisible
                        ? 'opacity 300ms ease, transform 350ms cubic-bezier(0.34, 1.56, 0.64, 1), border-color 200ms ease, background-color 200ms ease, color 200ms ease'
                        : 'none',
                      pointerEvents: isVisible ? 'auto' : 'none',
                      borderRadius: '20px',
                      border: `1.5px solid ${isSelected ? 'var(--fv3-accent-hover, #E8722A)' : '#E5E7EB'}`,
                      background: isSelected ? 'rgba(232, 114, 42, 0.08)' : 'white',
                      color: isSelected ? 'var(--fv3-accent-hover, #E8722A)' : '#6B7280',
                      fontSize: '12px',
                      fontWeight: 500,
                      padding: '4px 10px',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    {dept}
                  </button>
                );
              })}
            </div>

            {availableDepts.length > 0 && selectedDepts.length === 0 && (
              <p
                style={{
                  opacity: visibleCount >= availableDepts.length ? 1 : 0,
                  transform: visibleCount >= availableDepts.length ? 'translateY(0)' : 'translateY(-4px)',
                  transition: 'opacity 400ms ease, transform 400ms ease',
                }}
                className="text-xs text-slate-400 mt-2"
              >
                Select departments to calculate % of revenue
              </p>
            )}
          </div>
        </div>
      </div>}

      {/* Mini Chart */}
      {transformedStats && (
        <MiniChart
          data={transformedMonths}
          currentForecast={transformedForecast}
          stats={transformedStats}
          formatValue={formatValue}
        />
      )}

      {/* Stats Grid */}
      {transformedStats && (
        <StatsGrid stats={transformedStats} accountCount={data.accountCount} formatValue={formatValue} />
      )}

      {/* Context Note */}
      {viewMode === '$' ? (
        <ContextNote
          contextNote={data.insight.contextNote}
          statusType={data.insight.statusType}
        />
      ) : transformedInsight ? (
        <ContextNote
          contextNote={transformedInsight.insight.contextNote}
          statusType={transformedInsight.insight.statusType}
        />
      ) : null}
    </div>
  );
}

export default DepartmentAnalysisTab;
